"use client";

import { c as _c } from "react/compiler-runtime";
import { filterOptionLabelCacheInputKey, formatFilterValueLabel, getFilterOptionLabelCache, getFilterValueLabels, isFilterValueActive, isPendingAcademicScopeValue } from "@/components/filters/filter-values";
import { FilterDropdown, FilterLabelHydrator, StandaloneFilterDropdown } from "@/components/filters/FilterDropdown";
import type { FilterOptionsResolvedHandler } from "@/components/filters/FilterDropdown";
import { Button } from "@/components/ui/Button";
import { FilterBar, FilterBarAdd, FilterBarClear } from "@/components/ui/FilterBar";
import { FilterChip, FilterChipField, FilterChipOperator, FilterChipRemove, FilterChipValue } from "@/components/ui/FilterChip";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import type { AnyFilterConfig, AnyLabeledFilterConfig } from "@/types/list";
import { getFilterExclusions, moveFilterOperator, setFilterCondition } from "@/utils/negative-list-filtering";
import type { FilterOperator } from "@/utils/negative-list-filtering";
import { ChevronDown, ListFilterIcon } from "lucide-react";
import type { ComponentType } from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import cn from "twsx";
const emptyFilterInput: Record<string, unknown> = {};
const emptyFilterKeys: string[] = [];

/** "Status" → "statuses", "Agency" → "agencies", "Program" → "programs": the noun a collapsed multi-value chip counts. */
export function pluralizeFilterNoun(label: string) {
  const noun = label.toLowerCase();
  if (/[^aeiou]y$/u.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(?:s|x|ch|sh)$/u.test(noun)) return `${noun}es`;
  return `${noun}s`;
}
export function getFilterOperatorLabel(filterConfig: AnyLabeledFilterConfig, valueCount: number, operator: FilterOperator = "is") {
  if (operator === "isNot") return filterConfig.type === "input" ? "does not contain" : valueCount > 1 ? "is none of" : "is not";
  switch (filterConfig.type) {
    case "input":
      {
        return "contains";
      }
    case "multi-select":
      {
        return valueCount > 1 ? "is any of" : "is";
      }
    case "boolean":
    case "date":
    case "select":
      {
        return "is";
      }
    default:
      {
        return "is";
      }
  }
}
export function getActiveFilterClauses(filterConfigs: Record<string, AnyLabeledFilterConfig>, currentFilters: Record<string, unknown> | undefined, filterInput: Record<string, unknown>, hiddenFilterKeys: string[]) {
  if (!currentFilters) {
    return [];
  }
  const hiddenFilterKeySet = new Set(hiddenFilterKeys);
  return Object.entries(filterConfigs).flatMap(([filterKey, filterConfig]) => {
    if (hiddenFilterKeySet.has(filterKey)) {
      return [];
    }
    return (["is", "isNot"] as const).flatMap(operator => {
      const value = operator === "is" ? currentFilters[filterKey] : getFilterExclusions(currentFilters)[filterKey];
      if (!isFilterValueActive(filterConfig, value)) {
        return [];
      }
      const valueLabels = getFilterValueLabels(filterConfig, value, filterInput, filterKey);
      const resolvedValues = Array.isArray(value) ? value : [value];
      const values = resolvedValues.map((resolvedValue, index) => {
        const valueLabel = valueLabels[index] ?? "";
        return {
          key: `${filterKey}:${String(resolvedValue)}`,
          value: resolvedValue,
          valueLabel,
          pending: isPendingAcademicScopeValue(filterConfig, resolvedValue, filterInput, filterKey)
        };
      }).filter(resolvedValue => resolvedValue.valueLabel);
      if (values.length === 0) {
        return [];
      }
      return [{
        key: operator === "is" ? filterKey : `${filterKey}:isNot`,
        operator,
        filterKey,
        label: filterConfig.label,
        values
      }];
    });
  });
}
export function ListQueryControls(t0) {
  const $ = _c(88);
  const {
    filterConfigs,
    filterComponents,
    filterInput,
    currentFilters,
    onFilterChange,
    showTrigger: t1,
    showActiveClauses: t2,
    showStandaloneFilters: t3,
    hiddenFilterKeys: t4,
    standaloneFilterKeys: t5,
    className,
    triggerLabel: t6,
    wrapWithParent: t7
  } = t0;
  const showTrigger = t1 === undefined ? true : t1;
  const showActiveClauses = t2 === undefined ? true : t2;
  const showStandaloneFilters = t3 === undefined ? true : t3;
  const hiddenFilterKeys = t4 === undefined ? emptyFilterKeys : t4;
  const standaloneFilterKeys = t5 === undefined ? emptyFilterKeys : t5;
  const triggerLabel = t6 === undefined ? "Filter" : t6;
  const wrapWithParent = t7 === undefined ? false : t7;
  const [isInteractive, setIsInteractive] = useState(false);
  const hasFilterConfigs = Object.keys(filterConfigs).length > 0;
  let t8;
  let t9;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsInteractive(true);
    };
    t9 = [];
    $[0] = t8;
    $[1] = t9;
  } else {
    t8 = $[0];
    t9 = $[1];
  }
  useEffect(t8, t9);
  const resolvedFilterInput = filterInput ?? emptyFilterInput;
  let t10;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {};
    $[2] = t10;
  } else {
    t10 = $[2];
  }
  const [optionLabelCache, setOptionLabelCache] = useState(t10);
  let t11;
  if ($[3] !== optionLabelCache || $[4] !== resolvedFilterInput) {
    const seededLabelCache = getFilterOptionLabelCache(resolvedFilterInput) ?? {};
    const filterKeys = new Set([...Object.keys(seededLabelCache), ...Object.keys(optionLabelCache)]);
    t11 = {
      ...resolvedFilterInput,
      [filterOptionLabelCacheInputKey]: Object.fromEntries([...filterKeys].map(filterKey => [filterKey, {
        ...seededLabelCache[filterKey],
        ...optionLabelCache[filterKey]
      }]))
    };
    $[3] = optionLabelCache;
    $[4] = resolvedFilterInput;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  const filterInputWithLabelCache = t11;
  let t12;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (filterKey_0, options) => {
      setOptionLabelCache(previousCache => {
        const previousFilterCache = previousCache[filterKey_0] ?? {};
        const nextFilterCache = {
          ...previousFilterCache
        };
        let changed = false;
        options.forEach(option => {
          if (nextFilterCache[option.value] !== option.label) {
            nextFilterCache[option.value] = option.label;
            changed = true;
          }
        });
        if (!changed) {
          return previousCache;
        }
        return {
          ...previousCache,
          [filterKey_0]: nextFilterCache
        };
      });
    };
    $[6] = t12;
  } else {
    t12 = $[6];
  }
  const handleOptionsResolved = t12;
  const typedCurrentFilters = currentFilters as Record<string, unknown> | undefined;
  let t13;
  if ($[7] !== hiddenFilterKeys || $[8] !== standaloneFilterKeys) {
    t13 = [...hiddenFilterKeys, ...standaloneFilterKeys];
    $[7] = hiddenFilterKeys;
    $[8] = standaloneFilterKeys;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  const activeHiddenFilterKeys = t13;
  let t14;
  if ($[10] !== activeHiddenFilterKeys || $[11] !== filterConfigs || $[12] !== filterInputWithLabelCache || $[13] !== typedCurrentFilters) {
    t14 = getActiveFilterClauses(filterConfigs, typedCurrentFilters, filterInputWithLabelCache, activeHiddenFilterKeys);
    $[10] = activeHiddenFilterKeys;
    $[11] = filterConfigs;
    $[12] = filterInputWithLabelCache;
    $[13] = typedCurrentFilters;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const activeClauses = t14;
  let t15;
  if ($[15] !== activeClauses || $[16] !== onFilterChange || $[17] !== typedCurrentFilters) {
    t15 = () => {
      let nextFilters = {
        ...(typedCurrentFilters ?? {})
      };
      activeClauses.forEach(clause => {
        nextFilters = setFilterCondition(nextFilters, clause.filterKey, clause.operator, undefined);
      });
      onFilterChange?.(nextFilters as $Filters);
    };
    $[15] = activeClauses;
    $[16] = onFilterChange;
    $[17] = typedCurrentFilters;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const clearActiveClauses = t15;
  let t16;
  if ($[19] !== filterConfigs || $[20] !== onFilterChange || $[21] !== typedCurrentFilters) {
    t16 = values => {
      let next = values;
      for (const key of Object.keys(filterConfigs)) {
        if (values[key] !== typedCurrentFilters?.[key]) {
          next = setFilterCondition(next, key, "is", values[key]);
        }
      }
      onFilterChange?.(next as $Filters);
    };
    $[19] = filterConfigs;
    $[20] = onFilterChange;
    $[21] = typedCurrentFilters;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const handlePositiveChange = t16;
  let t17;
  if ($[23] !== hiddenFilterKeys || $[24] !== standaloneFilterKeys) {
    t17 = new Set([...hiddenFilterKeys, ...standaloneFilterKeys]);
    $[23] = hiddenFilterKeys;
    $[24] = standaloneFilterKeys;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const addFilterHiddenKeySet = t17;
  let t18;
  if ($[26] !== filterComponents || $[27] !== filterConfigs || $[28] !== filterInputWithLabelCache || $[29] !== showStandaloneFilters || $[30] !== standaloneFilterKeys || $[31] !== typedCurrentFilters) {
    t18 = showStandaloneFilters ? standaloneFilterKeys.flatMap(filterKey_1 => {
      const filterConfig = filterConfigs[filterKey_1];
      const filterComponent = filterComponents[filterKey_1];
      if (!filterConfig || !filterComponent) {
        return [];
      }
      const value = typedCurrentFilters?.[filterKey_1];
      const isActive = isFilterValueActive(filterConfig, value);
      const valueLabel = isActive ? formatFilterValueLabel(filterConfig, value, filterInputWithLabelCache, filterKey_1) : "";
      const pending = isActive && (Array.isArray(value) ? value : [value]).some(item => isPendingAcademicScopeValue(filterConfig, item, filterInputWithLabelCache, filterKey_1));
      return [{
        filterKey: filterKey_1,
        filterConfig,
        filterComponent,
        valueLabel,
        pending
      }];
    }) : [];
    $[26] = filterComponents;
    $[27] = filterConfigs;
    $[28] = filterInputWithLabelCache;
    $[29] = showStandaloneFilters;
    $[30] = standaloneFilterKeys;
    $[31] = typedCurrentFilters;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const standaloneFilters = t18;
  let t19;
  if ($[33] !== addFilterHiddenKeySet || $[34] !== filterConfigs) {
    t19 = Object.fromEntries(Object.entries(filterConfigs).filter(t20 => {
      const [filterKey_2] = t20;
      return !addFilterHiddenKeySet.has(filterKey_2);
    }));
    $[33] = addFilterHiddenKeySet;
    $[34] = filterConfigs;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const addFilterConfigs = t19 as Record<string, AnyLabeledFilterConfig>;
  let t20;
  if ($[36] !== addFilterConfigs) {
    t20 = Object.keys(addFilterConfigs);
    $[36] = addFilterConfigs;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const hasAddFilterConfigs = t20.length > 0;
  if (!hasFilterConfigs) {
    return null;
  }
  if (standaloneFilters.length === 0 && !showTrigger && !showActiveClauses) {
    return null;
  }
  let t21;
  if ($[38] !== activeClauses || $[39] !== filterComponents || $[40] !== filterConfigs || $[41] !== filterInputWithLabelCache || $[42] !== showActiveClauses || $[43] !== typedCurrentFilters) {
    t21 = showActiveClauses && activeClauses.map(clause_0 => {
      const filterConfig_0 = filterConfigs[clause_0.filterKey];
      const filterComponent_0 = filterComponents[clause_0.filterKey];
      if (!filterConfig_0?.hydrateSelectedOptionLabels || !filterComponent_0) {
        return null;
      }
      return <FilterLabelHydrator key={`label-hydrator:${clause_0.key}:${clause_0.values.map(_temp).join("|")}`} filterKey={clause_0.filterKey} filterConfig={filterConfig_0} filterComponent={filterComponent_0} filterInput={filterInputWithLabelCache} currentValues={clause_0.operator === "isNot" ? {
        ...typedCurrentFilters,
        [clause_0.filterKey]: getFilterExclusions(typedCurrentFilters)[clause_0.filterKey]
      } : typedCurrentFilters} onOptionsResolved={handleOptionsResolved} />;
    });
    $[38] = activeClauses;
    $[39] = filterComponents;
    $[40] = filterConfigs;
    $[41] = filterInputWithLabelCache;
    $[42] = showActiveClauses;
    $[43] = typedCurrentFilters;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== filterInputWithLabelCache || $[46] !== handlePositiveChange || $[47] !== standaloneFilters || $[48] !== typedCurrentFilters) {
    let t23;
    if ($[50] !== filterInputWithLabelCache || $[51] !== handlePositiveChange || $[52] !== typedCurrentFilters) {
      t23 = t24 => {
        const {
          filterKey: filterKey_3,
          filterConfig: filterConfig_1,
          filterComponent: filterComponent_1,
          valueLabel: valueLabel_0,
          pending: pending_0
        } = t24;
        return <Fragment key={filterKey_3}>{filterConfig_1.hydrateSelectedOptionLabels && <FilterLabelHydrator filterKey={filterKey_3} filterConfig={filterConfig_1} filterComponent={filterComponent_1} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onOptionsResolved={handleOptionsResolved} />}<StandaloneFilterDropdown filterKey={filterKey_3} filterConfig={filterConfig_1} filterComponent={filterComponent_1} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved}><Button variant="tertiary" size="lg" type="button" aria-label={`Change ${filterConfig_1.label}`} tooltip={`Change ${filterConfig_1.label}`} className="min-w-0 max-w-64" data-list-filter-scope="true">{filterConfig_1.icon}<span className={cn("shrink-0", valueLabel_0 && "list-filter-scope-label max-md:hidden")}>{`${filterConfig_1.label}${valueLabel_0 ? ":" : ""}`}</span>{pending_0 ? <Skeleton size="xs" shape="pill" className="w-16" /> : valueLabel_0 && <span className="min-w-0 truncate text-text-secondary">{valueLabel_0}</span>}<ChevronDown className="shrink-0" /></Button></StandaloneFilterDropdown></Fragment>;
      };
      $[50] = filterInputWithLabelCache;
      $[51] = handlePositiveChange;
      $[52] = typedCurrentFilters;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    t22 = standaloneFilters.map(t23);
    $[45] = filterInputWithLabelCache;
    $[46] = handlePositiveChange;
    $[47] = standaloneFilters;
    $[48] = typedCurrentFilters;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[54] !== activeClauses.length || $[55] !== addFilterConfigs || $[56] !== filterComponents || $[57] !== filterInputWithLabelCache || $[58] !== handlePositiveChange || $[59] !== hasAddFilterConfigs || $[60] !== isInteractive || $[61] !== showTrigger || $[62] !== triggerLabel || $[63] !== typedCurrentFilters) {
    t23 = showTrigger && hasAddFilterConfigs && <FilterDropdown listConfigFilters={addFilterConfigs as Record<string, AnyFilterConfig>} filterComponents={filterComponents} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved} groupSearchResults={true}>{triggerLabel ? <Button variant="tertiary" size="lg" iconOnlyBelowMd={true} type="button" aria-label={activeClauses.length > 0 ? `Add filter, ${activeClauses.length} active` : "Add filter"} tooltip="Add filter" disabled={!isInteractive}><ListFilterIcon /><span>{triggerLabel}</span>{activeClauses.length > 0 && <span className="list-filter-trigger-count" aria-hidden="true">{activeClauses.length}</span>}</Button> : <IconButton label={activeClauses.length > 0 ? `Add filter, ${activeClauses.length} active` : "Add filter"} variant="toolbar" size={32} disabled={!isInteractive}><ListFilterIcon /></IconButton>}</FilterDropdown>;
    $[54] = activeClauses.length;
    $[55] = addFilterConfigs;
    $[56] = filterComponents;
    $[57] = filterInputWithLabelCache;
    $[58] = handlePositiveChange;
    $[59] = hasAddFilterConfigs;
    $[60] = isInteractive;
    $[61] = showTrigger;
    $[62] = triggerLabel;
    $[63] = typedCurrentFilters;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== activeClauses || $[66] !== addFilterConfigs || $[67] !== clearActiveClauses || $[68] !== filterComponents || $[69] !== filterConfigs || $[70] !== filterInputWithLabelCache || $[71] !== handlePositiveChange || $[72] !== hasAddFilterConfigs || $[73] !== isInteractive || $[74] !== onFilterChange || $[75] !== showActiveClauses || $[76] !== typedCurrentFilters) {
    t24 = showActiveClauses && activeClauses.length > 0 && <FilterBar add={hasAddFilterConfigs && <FilterDropdown listConfigFilters={addFilterConfigs as Record<string, AnyFilterConfig>} filterComponents={filterComponents} filterInput={filterInputWithLabelCache} currentValues={typedCurrentFilters} onValuesChange={handlePositiveChange} onOptionsResolved={handleOptionsResolved} groupSearchResults={true}><FilterBarAdd disabled={!isInteractive} /></FilterDropdown>} clear={<FilterBarClear aria-label="Clear filters" disabled={!isInteractive} onClick={clearActiveClauses} />}>{activeClauses.map(clause_1 => {
        const filterConfig_2 = filterConfigs[clause_1.filterKey];
        const filterComponent_2 = filterComponents[clause_1.filterKey];
        if (!filterConfig_2 || !filterComponent_2) {
          return null;
        }
        const displayValues = clause_1.filterKey === "season" ? [...new Map(clause_1.values.map(_temp2)).values()] : clause_1.values;
        const pending_1 = displayValues.some(_temp3);
        const fullValueLabel = displayValues.filter(_temp4).map(_temp5).join(", ");
        const valueLabel_1 = displayValues.length > 1 ? `${displayValues.length} ${pluralizeFilterNoun(clause_1.label)}` : fullValueLabel;
        const operatorLabel = getFilterOperatorLabel(filterConfig_2, clause_1.values.length, clause_1.operator);
        const editValues = clause_1.operator === "isNot" ? {
          ...typedCurrentFilters,
          [clause_1.filterKey]: getFilterExclusions(typedCurrentFilters)[clause_1.filterKey]
        } : typedCurrentFilters;
        return <FilterChip key={clause_1.key}><FilterChipField>{clause_1.label}</FilterChipField><FilterChipOperator aria-label={`Set ${clause_1.label} operator to ${clause_1.operator === "is" ? "is not" : "is"}`} tooltip="Change filter operator" disabled={!isInteractive} onClick={() => {
            const next_0 = moveFilterOperator(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, clause_1.operator === "is" ? "isNot" : "is");
            onFilterChange?.(next_0 as $Filters);
          }}>{operatorLabel}</FilterChipOperator><StandaloneFilterDropdown filterKey={clause_1.filterKey} filterConfig={filterConfig_2} filterComponent={filterComponent_2} filterInput={filterInputWithLabelCache} currentValues={editValues} onValuesChange={values_0 => {
            onFilterChange?.(setFilterCondition(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, values_0[clause_1.filterKey]) as $Filters);
          }} onOptionsResolved={handleOptionsResolved}><FilterChipValue aria-label={`Edit ${clause_1.label} filter, ${operatorLabel} ${fullValueLabel}`} tooltip={pending_1 ? undefined : fullValueLabel} disabled={!isInteractive} pending={pending_1}>{valueLabel_1}</FilterChipValue></StandaloneFilterDropdown><FilterChipRemove label={`Remove ${clause_1.label} filter`} disabled={!isInteractive} onClick={() => {
            const nextFilters_0 = setFilterCondition(typedCurrentFilters ?? {}, clause_1.filterKey, clause_1.operator, undefined);
            onFilterChange?.(nextFilters_0 as $Filters);
          }} /></FilterChip>;
      })}</FilterBar>;
    $[65] = activeClauses;
    $[66] = addFilterConfigs;
    $[67] = clearActiveClauses;
    $[68] = filterComponents;
    $[69] = filterConfigs;
    $[70] = filterInputWithLabelCache;
    $[71] = handlePositiveChange;
    $[72] = hasAddFilterConfigs;
    $[73] = isInteractive;
    $[74] = onFilterChange;
    $[75] = showActiveClauses;
    $[76] = typedCurrentFilters;
    $[77] = t24;
  } else {
    t24 = $[77];
  }
  let t25;
  if ($[78] !== t21 || $[79] !== t22 || $[80] !== t23 || $[81] !== t24) {
    t25 = <>{t21}{t22}{t23}{t24}</>;
    $[78] = t21;
    $[79] = t22;
    $[80] = t23;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  const controls = t25;
  if (wrapWithParent) {
    return controls;
  }
  let t26;
  if ($[83] !== className) {
    t26 = cn("flex min-w-0 flex-wrap items-center gap-2", className);
    $[83] = className;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== controls || $[86] !== t26) {
    t27 = <div className={t26}>{controls}</div>;
    $[85] = controls;
    $[86] = t26;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  return t27;
}
function _temp5(value_4) {
  return value_4.valueLabel;
}
function _temp4(value_3) {
  return !value_3.pending;
}
function _temp3(value_2) {
  return value_2.pending;
}
function _temp2(value_1) {
  return [value_1.pending ? value_1.key : value_1.valueLabel, value_1];
}
function _temp(value_0) {
  return String(value_0.value);
}